import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Banknote, Bell, Building2, CheckSquare2, ChevronDown, CreditCard, FileText, GitBranch, KeyRound, LayoutDashboard, LogOut, Menu, PackageCheck, Settings, ShieldCheck, Users, Workflow, X } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import type { Permission } from '@/types/domain';
import { ChangePasswordDialog } from '@/features/auth/ChangePasswordDialog';
import { useMyWorkflowTasks } from '@/hooks/useWorkflows';
import { NotificationBell } from '@/features/notifications/NotificationBell';

const navGroups: { label: string; items: { label: string; to: string; icon: typeof Bell; permission?: Permission; anyPermission?: Permission[]; end?: boolean }[] }[] = [
  { label: 'Workspace', items: [
    { label: 'Overview', to: '/app', icon: LayoutDashboard, end: true },
    { label: 'Vendors', to: '/app/vendors', icon: Building2, anyPermission: ['VENDOR.VIEW', 'VENDOR.CREATE', 'VENDOR.APPROVE'] },
    { label: 'Vendor invites', to: '/app/vendor-invites', icon: Users, permission: 'VENDOR.CREATE' },
    { label: 'Purchase orders', to: '/app/purchase-orders', icon: FileText },
    { label: 'Goods receipts', to: '/app/goods-receipts', icon: PackageCheck },
    { label: 'My approvals', to: '/app/approvals', icon: CheckSquare2 },
    { label: 'Notifications', to: '/app/notifications', icon: Bell },
  ] },
  { label: 'Finance', items: [
    { label: 'Advances', to: '/app/finance/advances', icon: Banknote, permission: 'PAYMENT.VIEW' },
    { label: 'Payments', to: '/app/payments', icon: CreditCard, permission: 'PAYMENT.VIEW' },
  ] },
  { label: 'Configure', items: [
    { label: 'Workflows', to: '/app/workflows', icon: Workflow, permission: 'WORKFLOW.CONFIGURE' },
    { label: 'Users & roles', to: '/app/users', icon: ShieldCheck, permission: 'USER.MANAGE' },
    { label: 'Audit log', to: '/app/audit', icon: GitBranch, permission: 'USER.MANAGE' },
  ] },
];

export function AppLayout() {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const { user, can, logout: endSession } = useAuth();
  const pendingTasks = useMyWorkflowTasks({ page: 1, pageSize: 1 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!profileOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [profileOpen]);

  if (!user) return null;
  const displayName = user.fullName;
  const initials = displayName.split(' ').map((part) => part[0]).slice(0, 2).join('').toUpperCase();
  const roleLabel = user.roles.join(', ') || user.userType;
  const logout = async () => {
    await endSession();
    setProfileOpen(false);
    navigate('/login', { replace: true });
  };
  return <div className="app-shell">
    {open && <button className="sidebar-scrim" onClick={() => setOpen(false)} aria-label="Close navigation" />}
    <aside className={`sidebar ${open ? 'is-open' : ''}`}>
      <div className="brand"><span className="brand__mark">V</span><div><strong>Vendiz</strong></div><button className="mobile-close" onClick={() => setOpen(false)}><X /></button></div>
      <nav>{navGroups.map((group) => <div className="nav-group" key={group.label}><span className="nav-group__label">{group.label}</span>{group.items.filter((item) => (!item.permission || can(item.permission)) && (!item.anyPermission || item.anyPermission.some(can))).map((item) => <NavLink end={item.end} to={item.to} key={item.to} onClick={() => setOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}><Icon icon={item.icon} /><span>{item.label}</span>{item.label === 'My approvals' && Boolean(pendingTasks.data?.totalCount) && <b className="nav-count">{pendingTasks.data?.totalCount}</b>}</NavLink>)}</div>)}</nav>
      <div className="sidebar-footer"><NavLink to="/app/settings/company"><Settings size={18} /> Settings</NavLink></div>
    </aside>
    <div className="app-main">
      <header className="topbar"><button className="menu-button" onClick={() => setOpen(true)}><Menu /></button><div className="topbar-actions"><NotificationBell /><span className="topbar-divider" /><div className="profile-menu" ref={profileMenuRef}><button className="profile-trigger" onClick={() => setProfileOpen((value) => !value)} aria-expanded={profileOpen} aria-haspopup="menu"><Avatar initials={initials} name={displayName} /><span className="user-copy"><strong>{displayName}</strong><span>{roleLabel}</span></span><ChevronDown size={16} className={profileOpen ? 'is-open' : ''} /></button>{profileOpen && <div className="profile-dropdown" role="menu"><div className="profile-dropdown__identity"><strong>{displayName}</strong><span>{roleLabel}</span></div><button role="menuitem" onClick={() => { setProfileOpen(false); setPasswordOpen(true); }}><KeyRound size={17} /> Change password</button><button role="menuitem" onClick={logout}><LogOut size={17} /> Log out</button></div>}</div></div></header>
      <main className="content"><Outlet /></main>
    </div>
    <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
  </div>;
}
